// The two tabs differ only in which pair of fields they read and write.
export const CHANNELS = {
  email: {
    key: "email",
    enabledField: "emailEnabled",
    defaultField: "defaultEmail",
  },
  browser: {
    key: "browser",
    enabledField: "browserEnabled",
    defaultField: "defaultBrowser",
  },
};

// BE serialises camelCase (System.Text.Json's default policy, applied to the same
// ResponseModel the feed uses). The integration doc's examples were written in
// PascalCase and have since been corrected — reading both costs one line and means
// a stale tenant or a hand-written fixture cannot render the screen blank.
const pick = (source, key, fallback) => {
  if (!source || typeof source !== "object") return fallback;

  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  const value = source[key] !== undefined ? source[key] : source[pascal];

  return value === undefined ? fallback : value;
};

const normalizeNotification = (notification) => ({
  eventTypeCode: pick(notification, "eventTypeCode", ""),
  displayName: pick(notification, "displayName", ""),
  description: pick(notification, "description", ""),
  moduleCode: pick(notification, "moduleCode", ""),
  groupCode: pick(notification, "groupCode", ""),
  browserEnabled: Boolean(pick(notification, "browserEnabled", false)),
  emailEnabled: Boolean(pick(notification, "emailEnabled", false)),
  defaultBrowser: Boolean(pick(notification, "defaultBrowser", false)),
  defaultEmail: Boolean(pick(notification, "defaultEmail", false)),
  // false means the toggle is still showing a catalog default nobody has chosen.
  isUserSet: Boolean(pick(notification, "isUserSet", false)),
  sortOrder: Number(pick(notification, "sortOrder", 0)),
});

const normalizeGroup = (group) => ({
  groupCode: pick(group, "groupCode", ""),
  groupName: pick(group, "groupName", ""),
  notifications: (pick(group, "notifications", []) || []).map(
    normalizeNotification,
  ),
});

// Groups is [] for a module seeded with no notification types, and null on the
// Modules endpoint. Both mean "nothing to list", never "hide the module".
const normalizeModule = (module) => ({
  moduleCode: pick(module, "moduleCode", ""),
  moduleName: pick(module, "moduleName", ""),
  enabled: Boolean(pick(module, "enabled", false)),
  source: pick(module, "source", ""),
  sortOrder: Number(pick(module, "sortOrder", 0)),
  groups: (pick(module, "groups", []) || []).map(normalizeGroup),
});

// Every write answers with the tree that landed, but under a different key each
// time (settings, or the payload itself on a read). Anything that is not one of
// those is not an empty screen — it is a shape we did not expect, and rendering
// it as empty would wipe a screen the server never asked us to change.
export const hasSettingsShape = (value) =>
  Boolean(value) &&
  typeof value === "object" &&
  (value.modules !== undefined ||
    value.Modules !== undefined ||
    value.channels !== undefined ||
    value.Channels !== undefined);

// The API already sorts by SortOrder, so array order is the render order.
export const normalizeSettings = (data) => {
  const channels = pick(data, "channels", {}) || {};

  return {
    channels: {
      email: Boolean(pick(channels, "email", false)),
      browser: Boolean(pick(channels, "browser", false)),
    },
    search: pick(data, "search", null),
    modules: (pick(data, "modules", []) || []).map(normalizeModule),
  };
};

const matches = (value, term) =>
  String(value || "")
    .toLowerCase()
    .includes(term);

// Mirrors the server's ?search= rules so the two cannot drift: a module matching
// by its own name keeps all of its types, a module matching only through its types
// keeps just those, and a module matching nothing drops out entirely.
export const filterModules = (modules = [], search = "") => {
  const term = search.trim().toLowerCase();
  if (!term) return modules;

  return modules.reduce((kept, module) => {
    if (matches(module.moduleName, term)) {
      kept.push(module);
      return kept;
    }

    const groups = module.groups
      .map((group) => ({
        ...group,
        notifications: matches(group.groupName, term)
          ? group.notifications
          : group.notifications.filter(
              (notification) =>
                matches(notification.displayName, term) ||
                matches(notification.description, term),
            ),
      }))
      .filter((group) => group.notifications.length > 0);

    if (groups.length > 0) kept.push({ ...module, groups });
    return kept;
  }, []);
};

export const countNotifications = (modules = []) =>
  modules.reduce(
    (total, module) =>
      total +
      module.groups.reduce(
        (groupTotal, group) => groupTotal + group.notifications.length,
        0,
      ),
    0,
  );

export const findNotification = (modules = [], eventTypeCode) => {
  for (const module of modules) {
    for (const group of module.groups) {
      const found = group.notifications.find(
        (notification) => notification.eventTypeCode === eventTypeCode,
      );
      if (found) return found;
    }
  }

  return null;
};

// A save is a full state per type, so the channel that did not move is sent as it
// already stands. Sending only the changed half would read as "leave the other one
// off" on a type the user had switched on.
export const preferencePayload = (notification, channel, nextValue) => ({
  eventTypeCode: notification.eventTypeCode,
  browserEnabled:
    channel === "browser" ? nextValue : notification.browserEnabled,
  emailEnabled: channel === "email" ? nextValue : notification.emailEnabled,
});

// Optimistic echo of the save, replaced wholesale by the settings tree the
// response carries. Without it a toggle waits on a round trip to move.
export const applyToggle = (
  modules = [],
  eventTypeCode,
  channel,
  nextValue,
) => {
  const field = CHANNELS[channel].enabledField;

  return modules.map((module) => ({
    ...module,
    groups: module.groups.map((group) => ({
      ...group,
      notifications: group.notifications.map((notification) =>
        notification.eventTypeCode === eventTypeCode
          ? { ...notification, [field]: nextValue, isUserSet: true }
          : notification,
      ),
    })),
  }));
};
